import React, { useMemo, useState } from 'react';
import TopNavBar from '../../components/TopNavBar/TopNavBar';
import styles from './Labs.module.css';

type SketchConfig = {
  id: string;
  title: string;
  description: string;
  iframeSrc: string;
  instructions?: string;
};

const sketches: SketchConfig[] = [
  {
    id: 'slinky',
    title: 'Slinky',
    description: 'Canvas-based ribbon trails you can drag around the screen.',
    iframeSrc: '/d3-playground/Slinky/index.html',
    instructions: 'Move your cursor to steer the center; the ribbons follow smoothly.',
  },
  {
    id: 'kaleidoscope',
    title: 'Kaleidoscope',
    description: 'Drag the sampler triangle over an image to update the kaleidoscope.',
    iframeSrc: '/d3-playground/Kaleidoscope/index.html',
    instructions: 'Slide the triangle to sample different parts of the image and reflect them.',
  },
  {
    id: 'snowflake',
    title: 'Snowflake Generator',
    description: 'Adjust the cut points, then unfold the paper to reveal a snowflake.',
    iframeSrc: '/d3-playground/SnowFlakeGenerator/index.html',
    instructions: 'Drag the dots inside the triangle, click Create Snowflake, then reset to try again.',
  },
  {
    id: 'snowflake-6',
    title: 'Snowflake Generator (6-sided)',
    description: 'Make a 6-fold snowflake with a slimmer wedge.',
    iframeSrc: '/d3-playground/SnowFlakeGenerator/snowflake-6.html',
    instructions: 'Drag the dots inside the 60° wedge, click Create Snowflake, then reset to try again.',
  },
];

const Labs: React.FC = () => {
  const [selectedId, setSelectedId] = useState(sketches[0]?.id);
  const selectedSketch = useMemo(
    () => sketches.find((s) => s.id === selectedId) ?? sketches[0],
    [selectedId],
  );

  return (
    <div className={styles.page}>
      <TopNavBar />
      <main className={styles.content}>
        <header className={styles.header}>
          <div>
            <h1>Interactive Labs</h1>
            <p>Choose a sketch to load it in the viewport. Great for calm, exploratory play.</p>
          </div>
          <div className={styles.controls}>
            <label htmlFor="sketchSelect">Choose a sketch:</label>
            <select
              id="sketchSelect"
              value={selectedSketch?.id}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {sketches.map((sketch) => (
                <option key={sketch.id} value={sketch.id}>
                  {sketch.title}
                </option>
              ))}
            </select>
          </div>
        </header>

        <section className={styles.layout}>
          <div className={styles.viewer}>
            <div className={styles.viewport}>
              {selectedSketch?.iframeSrc ? (
                <iframe
                  key={selectedSketch.id}
                  src={selectedSketch.iframeSrc}
                  title={selectedSketch.title}
                  className={styles.iframe}
                  sandbox="allow-scripts allow-same-origin"
                />
              ) : (
                <div className={styles.placeholder}>
                  <p>Add an <code>iframeSrc</code> to this sketch to load your D3 page here.</p>
                  {selectedSketch?.instructions && <p>{selectedSketch.instructions}</p>}
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Labs;
